import React from "react";
import PaymentMethod from "../../../../../../utilities/data/model/PaymentMethod";
import Sale from "../../../../../../utilities/data/model/Sale";
import { PaymentScreenProps } from "../../PaymentScreen";
import {
  HttpStatusCode,
  PosApiEventListener,
  ReceiptAutoPrint,
  ReceiptType,
  ReprintType,
  TxnType,
  useLinklyPosContext,
} from "../../../../../../utilities/linkly";
import { Spacings, Image, View } from "react-native-ui-lib";
import { usePaymentControllerProvider } from "../../../../utils/payment-controller";
import {
  Button,
  LoadingIndicator,
  Stack,
  Text,
  useThemeProvider,
} from "@towersystems/roam-common-native";
import { moneyToFormattedString } from "@towersystems/roam-common/lib/shared-utils";
import {
  PurchaseRequest,
  RefundRequest,
  ReprintReceiptRequest,
  SendKeyRequest,
} from "../../../../../../utilities/linkly/models";
import {
  UiConstants,
  noopApiEventListener,
} from "../../../../../../utilities/linkly/constants";
import { usePrint } from "../../../../../../utilities/print";
import { useAssets } from "expo-asset";
import { toHtmlPage } from "../../../../../../utilities/to-html-page";
import { convertCentsToDollars } from "../../../../../../utilities/linkly/utils";
import { AddPaymentOptions } from "../../../../utils/payment-controller/types";
import {
  LinklyConfiguration,
  ReceiptAutoPrintType,
} from "@towersystems/roam-common/lib/generated-types";
import { useLazyQuery } from "@apollo/client";
import { useLinklyPowerRecoveryContext } from "../../../../../../utilities/linkly/useLinklyPowerRecovery";
import { TransactionStatusRequest } from "../../../../../../utilities/linkly/models/transaction/transaction-status-request";
import { QUERY_LINKLY_CONFIGURATION } from "../../../../../settings/common/constants";
import log, { LogSeverity } from "../../../../../../utilities/linkly/logger";

export interface MakeLinklyPaymentProps {
  paymentMethod: PaymentMethod;
  sale: Sale;
  navigation: PaymentScreenProps["navigation"];
}

type ButtonAction = {
  visible: boolean;
  content: string;
  enabled: boolean;
};

const DEFAULT_ACTION_BUTTON: ButtonAction = {
  visible: false,
  content: "",
  enabled: false,
};

export const MakeLinklyPayment = ({
  navigation,
  sale,
  paymentMethod,
}: MakeLinklyPaymentProps) => {
  const [configuration, setConfiguration] = React.useState<
    LinklyConfiguration | undefined
  >(undefined);
  const linklyPowerRecovery = useLinklyPowerRecoveryContext();
  const [displayText, setDisplayText] = React.useState<string[][]>([]);
  const [responseText, setResponseText] = React.useState<string | undefined>(
    undefined
  );

  const [backButton, setBackButton] = React.useState<ButtonAction>({
    visible: false,
    content: "Back",
    enabled: false,
  });

  const [errorText, setErrorText] = React.useState<string | undefined>();
  const [showLoading] = React.useState<boolean>(false);

  const [tryAgainButton, setTryAgainButton] = React.useState<ButtonAction>({
    visible: false,
    content: "Try Again",
    enabled: true,
  });

  const [reprintReceiptButton, setReprintReceiptButton] =
    React.useState<ButtonAction>({
      visible: false,
      content: "Reprint Receipt",
      enabled: false,
    });

  const [q] = useLazyQuery<{
    linklyConfiguration: LinklyConfiguration;
  }>(QUERY_LINKLY_CONFIGURATION);

  const [assets] = useAssets([
    require("../../../../../../../assets/images/linkly-logo-white.png"),
  ]);

  const { print } = usePrint();

  const [btnDisplayAction1, setBtnDisplayAction1] =
    React.useState<ButtonAction>(DEFAULT_ACTION_BUTTON);

  const [btnDisplayAction2, setBtnDisplayAction2] =
    React.useState<ButtonAction>(btnDisplayAction1);

  const { colors } = useThemeProvider();

  const {
    tenderedAmountInput,
    validTenderedAmount,
    addPayment,
    tenderedAmountAsInteger,
    isRefundAmount,
  } = usePaymentControllerProvider();

  React.useEffect(() => {
    navigation.setOptions({
      headerLeft: () => null,
      cardStyle: {
        backgroundColor: isRefundAmount ? colors.tyroRefund : colors.tyro,
      },
      headerStyle: {
        backgroundColor: isRefundAmount ? colors.tyroRefund : colors.tyro,
        borderBottomColor: "rgba(255,255,255,0.2)",
      },
      headerTitle: () => null,
    });
  }, [colors, isRefundAmount]);

  const linklyContext = useLinklyPosContext();

  const [sessionId, setSessionId] = React.useState<string | undefined>(
    undefined
  );

  const handleOnError: PosApiEventListener["onError"] = (_, error, ___) => {
    const message = error.message
      ? error.httpStatusCode
        ? `Http error ${error.httpStatusCode}, ${error.message}`
        : `${error.message}`
      : `Http error ${error.httpStatusCode}`;

    setErrorText(message);
    // reset the ui.

    // reset the buttons
    setBtnDisplayAction2((prev) => ({ ...prev, enabled: true }));
    setBtnDisplayAction1((prev) => ({ ...prev, enabled: true }));

    if (linklyPowerRecovery.lastSessionInterrupted) {
      setTryAgainButton((prev) => ({
        ...prev,
        enabled: true,
        visible: true,
      }));
      setDisplayText([]);
      setResponseText(undefined);
      setBackButton((prev) => ({
        ...prev,
        enabled: true,
        visible: true,
      }));
    }

    if (
      error.httpStatusCode &&
      [HttpStatusCode.NotFound, HttpStatusCode.Unauthorized].includes(
        error.httpStatusCode
      )
    ) {
      setBackButton((prev) => ({
        ...prev,
        enabled: true,
        visible: true,
      }));

      // transaction never actually started
      linklyPowerRecovery.clearTransaction();
    }

    if (_ instanceof PurchaseRequest || _ instanceof RefundRequest) {
      setBackButton((prev) => ({
        ...prev,
        enabled: true,
        visible: true,
      }));
      linklyPowerRecovery.clearTransaction();
    }
  };

  const handleOnReceipt: PosApiEventListener["onReceipt"] = (
    _,
    __,
    response
  ) => {
    const receiptText = response.ReceiptText.map((s) => s.trim()).join("\n\n");
    switch (response.Type) {
      case ReceiptType.Merchant:
      case ReceiptType.Settlement:
        print(toHtmlPage(receiptText));
        break;
      case ReceiptType.Customer:
        // do nothing
        // they will returned when the transaction is complete
        break;
    }
  };

  const handleOnReceiptReprintComplete: PosApiEventListener["onReceiptReprintComplete"] =
    (_, __, response) => {
      setBackButton((prev) => ({
        ...prev,
        enabled: true,
        visible: true,
      }));

      setTryAgainButton((prev) => ({
        ...prev,
        enabled: false,
        visible: false,
      }));

      setReprintReceiptButton((prev) => ({
        ...prev,
        enabled: true,
        visible: true,
      }));

      if (response.Success === true) {
        //setResponseText("Reprint request Successful");

        const receiptText = response.ReceiptText.map((s) => s.trim()).join(
          "\n\n"
        );

        print(toHtmlPage(receiptText));
      }
    };

  const handleOnDisplay: PosApiEventListener["onDisplay"] = (
    _,
    __,
    response
  ) => {
    setDisplayText((prev) => [
      ...prev,
      response.DisplayText.map((s) => s.trim()),
    ]);

    setBtnDisplayAction1(DEFAULT_ACTION_BUTTON);
    setBtnDisplayAction2(DEFAULT_ACTION_BUTTON);

    if (response.OkKeyFlag) {
      setBtnDisplayAction1((b) => ({
        ...b,
        content: UiConstants.Ok,
        visible: true,
        enabled: true,
      }));
    } else if (response.AcceptYesKeyFlag) {
      setBtnDisplayAction1((b) => ({
        ...b,
        content: UiConstants.Yes,
        visible: true,
        enabled: true,
      }));
    } else if (response.AuthoriseKeyFlag) {
      setBtnDisplayAction1((b) => ({
        ...b,
        content: UiConstants.Authorise,
        visible: true,
        enabled: true,
      }));
    }

    if (response.CancelKeyFlag) {
      setBtnDisplayAction2((b) => ({
        ...b,
        content: UiConstants.Cancel,
        visible: true,
        enabled: true,
      }));
    } else if (response.DeclineNoKeyFlag) {
      setBtnDisplayAction2((b) => ({
        ...b,
        content: UiConstants.No,
        visible: true,
        enabled: true,
      }));
    }
  };

  const handleTransactionCompleteCallback: PosApiEventListener["onTransactionComplete"] =
    async (_, __, response) => {
      setResponseText(response.ResponseText?.trim() ?? "");
      // reset ui
      setBtnDisplayAction1(DEFAULT_ACTION_BUTTON);
      setBtnDisplayAction2(DEFAULT_ACTION_BUTTON);
      setErrorText(undefined);

      if (response.Success === false) {
        if (response.Receipts) {
          for (const receipt of response.Receipts) {
            if (receipt.Type === ReceiptType.Customer) {
              print(
                toHtmlPage(
                  receipt.ReceiptText.map((s) => s.trim()).join("\n\n")
                )
              );
            }
          }
        }

        await linklyPowerRecovery.clearTransaction();
        setBackButton((prev) => ({
          ...prev,
          enabled: true,
          visible: true,
        }));
        return;
      }

      try {
        let paymentAmount;
        let surchargeAmount;

        console.log("RESPONSE", response);

        if (response.TxnType === TxnType.Refund) {
          paymentAmount = -Math.abs(
            convertCentsToDollars(response.AmountTotal)
          );
        } else if (response.TxnType === TxnType.Purchase) {
          paymentAmount = convertCentsToDollars(response.AmountTotal);

          surchargeAmount = response.Surcharge
            ? convertCentsToDollars(response.Surcharge)
            : undefined;
        } else {
          throw new Error("Unknown transaction type");
        }

        const maybeCustomerReceipts = response.Receipts.filter(
          (r) => r.Type === ReceiptType.Customer
        )
          .map((r) => r.ReceiptText.map((s) => s.trim()).join("\n\n"))
          .reduce((acc, curr) => {
            if (curr.length > acc.length) {
              return curr;
            }
            return acc;
          }, "");

        const options: AddPaymentOptions = {
          tenderedAmount: paymentAmount,
          surcharge: surchargeAmount,
          name: "Linkly",
          transactionId: response.TxnRef,
          metadata: {
            response,
            customerReceipt: maybeCustomerReceipts
              ? toHtmlPage(maybeCustomerReceipts)
              : undefined,
          },
        };

        await addPayment(paymentMethod, options);
        // clear the transaction
        await linklyPowerRecovery.clearTransaction();

        const paidOff = await sale.balancePaidOff_ASYNC();

        if (paidOff) {
          navigation.navigate("CheckoutSummaryRootScreen", {
            screen: "CheckoutReviewScreen",
          });
        } else {
          navigation.navigate("SelectPaymentMethodScreen");
        }
      } catch (err) {
        log(`Error finalising transaction`, LogSeverity.Error, { err });
        setErrorText(
          err instanceof Error ? err.message : "Failed to finalise the payment"
        );
        setBackButton((prev) => ({ ...prev, enabled: true, visible: true }));
      }
    };

  const handleOnTransactionStatusRequestComplete: PosApiEventListener["onTransactionStatusRequestComplete"] =
    async (_, __, response) => {
      // The pinpad returns an empty result when there is no recoverable
      // transaction. Without a boolean Success it must NOT fall through to the
      // completion handler (which throws "Unknown transaction type" and gets
      // swallowed, leaving the screen stuck on a spinner with no way out).
      if (typeof response.Success !== "boolean") {
        setResponseText("No transaction found to recover.");
        await linklyPowerRecovery.clearTransaction();
        setBackButton((prev) => ({
          ...prev,
          enabled: true,
          visible: true,
        }));
        return;
      }

      if (response.Success === false) {
        setResponseText(
          `Transaction Not Completed: ${response.ResponseText?.trim() ?? ""}`
        );

        const maybeCustomerReceipts = response.Receipts.filter(
          (r) => r.Type === ReceiptType.Customer
        )
          .map((r) => r.ReceiptText.map((s) => s.trim()).join("\n\n"))
          .reduce((acc, curr) => {
            if (curr.length > acc.length) {
              return curr;
            }
            return acc;
          }, "");

        const options: AddPaymentOptions = {
          tenderedAmount: 0,
          surcharge: 0,
          name: "Linkly",
          transactionId: response.TxnRef,
          metadata: {
            response,
            customerReceipt: maybeCustomerReceipts
              ? toHtmlPage(maybeCustomerReceipts)
              : undefined,
          },
          paymentState: "Error",
        };
        await addPayment(paymentMethod, options);
        await linklyPowerRecovery.clearTransaction();
        setBackButton((prev) => ({
          ...prev,
          enabled: true,
          visible: true,
        }));

        return printLastReceipt();
      } else {
        return handleTransactionCompleteCallback(_, __, response);
      }
    };
  React.useEffect(() => {
    linklyContext.methods.setEventListener({
      ...noopApiEventListener,
      onDisplay: handleOnDisplay,
      onError: handleOnError,
      onReceipt: handleOnReceipt,
      onTransactionComplete: handleTransactionCompleteCallback,
      onTransactionStatusRequestComplete:
        handleOnTransactionStatusRequestComplete,
      onReceiptReprintComplete: handleOnReceiptReprintComplete,
      onResultComplete: (...p) => {
        console.log("onResultComplete", p);
      },
    });
    return () => {
      linklyContext.methods.setEventListener(noopApiEventListener);
    };
  }, []);

  async function printLastReceipt() {
    //setResponseText("Printing Last Receipt...");
    setBackButton((prev) => ({
      ...prev,
      enabled: false,
      visible: true,
    }));

    setReprintReceiptButton((prev) => ({
      ...prev,
      enabled: false,
    }));

    const request = new ReprintReceiptRequest();
    request.ReprintType = ReprintType.GetLast;

    await linklyContext.methods.sendReprintReceiptRequest(request);
  }

  async function handleCancelRecovery() {
    // Escape hatch: clear the stored interrupted transaction so recovery does not
    // re-open on the next refresh, then leave the screen.
    await linklyPowerRecovery.clearTransaction();
    navigation.goBack();
  }

  async function doPowerRecovery() {
    if (
      linklyPowerRecovery.lastSessionInterrupted &&
      linklyPowerRecovery.transaction &&
      sale.id == linklyPowerRecovery.transaction.saleId &&
      paymentMethod.id == linklyPowerRecovery.transaction.paymentMethodId
    ) {
      setErrorText(undefined);
      setTryAgainButton((prev) => ({
        ...prev,
        enabled: false,
      }));
      setResponseText("Getting Last Transaction Result...");

      const statusRequest = new TransactionStatusRequest({
        SessionId: linklyPowerRecovery.transaction.sessionId,
      });

      await linklyContext.methods.sendTransactionStatusRequest(statusRequest);
      return;
    }
  }

  React.useEffect(() => {
    (async () => {
      if (
        linklyPowerRecovery.lastSessionInterrupted &&
        linklyPowerRecovery.transaction &&
        sale.id == linklyPowerRecovery.transaction.saleId &&
        paymentMethod.id == linklyPowerRecovery.transaction.paymentMethodId
      ) {
        await doPowerRecovery();
        return;
      }

      const result = await q({
        variables: {
          paymentMethodId: paymentMethod.id,
        },
      });

      if (!result.data?.linklyConfiguration) {
        setDisplayText([]);
        setErrorText("No linkly configuration found");
        return;
      }

      setConfiguration(result.data.linklyConfiguration);

      if (tenderedAmountAsInteger && validTenderedAmount) {
        let request: PurchaseRequest | RefundRequest;

        if (isRefundAmount) {
          request = new RefundRequest({
            AmountPurchase: Math.abs(tenderedAmountAsInteger),
          });
        } else {
          request = new PurchaseRequest({
            AmountPurchase: tenderedAmountAsInteger,
          });
        }
        request.ReceiptAutoPrint = toReceiptAutoPrintEnumValue(
          result.data.linklyConfiguration
        );
        request.TxnRef = generateTxnRef();
        request.TrainingMode = false;

        try {
          const _sessionId = await linklyContext.methods.sendTransactionRequest(
            request
          );

          setSessionId(_sessionId);
          await linklyPowerRecovery.saveTransaction(
            _sessionId,
            sale.id,
            paymentMethod.id,
            tenderedAmountAsInteger
          );
        } catch (err: unknown) {
          if (err instanceof Error) {
            setErrorText(err.message);

            if (err.message == "Pairing is required") {
              setBackButton((prev) => ({
                ...prev,
                enabled: true,
                visible: true,
              }));
            }
          } else {
            setErrorText("Unknown error");
          }
        }
      }
    })();
  }, []);

  const updateResult = (result: any) => {};

  function btnDisplayAction1Click() {
    return handleSendKey(
      btnDisplayAction1,
      UiConstants.Yes,
      UiConstants.OkCancelKey,
      UiConstants.YesKey
    );
  }

  function btnDisplayAction2Click() {
    return handleSendKey(
      btnDisplayAction2,
      UiConstants.No,
      UiConstants.OkCancelKey,
      UiConstants.NoKey
    );
  }

  function handleSendKey(
    button: ButtonAction,
    content: string,
    defaultValue: string,
    otherValue: string
  ) {
    const key = button.content === content ? otherValue : defaultValue;

    const keyRequest = new SendKeyRequest({ Key: key });

    if (!sessionId) {
      return;
    }

    keyRequest.SessionId = sessionId;

    setErrorText(undefined);
    linklyContext.methods.sendKeyRequest(keyRequest);
    setBtnDisplayAction2((prev) => ({ ...prev, enabled: false }));
    setBtnDisplayAction1((prev) => ({ ...prev, enabled: false }));
  }

  const displayTextMarkup =
    displayText.length >= 1 &&
    displayText[displayText.length - 1].map((text, index) => (
      <Text
        style={{
          textAlign: "center",
          fontSize: 18,
          marginBottom: Spacings.s6,
          color: colors.white,
        }}
        key={index}
      >
        {text}
      </Text>
    ));

  const errorTextMarkup = errorText ? (
    <Text
      style={{
        textAlign: "center",
        fontSize: 18,
        marginBottom: Spacings.s6,
        color: colors.white,
      }}
    >
      {errorText}
    </Text>
  ) : null;

  const btn1Markup = btnDisplayAction1?.visible ? (
    <Button
      style={{ minWidth: 120, backgroundColor: "#fff" }}
      disabled={
        !btnDisplayAction1.enabled ||
        !btnDisplayAction1.visible ||
        !btnDisplayAction1.content
      }
      onPress={() => btnDisplayAction1Click()}
    >
      <Text
        fontWeight="bold"
        style={{
          color: "#2b32b2",
          fontFamily: "Gilmer-Bold",
          fontSize: 17,
        }}
      >
        {btnDisplayAction1.content}
      </Text>
    </Button>
  ) : null;

  const btn2Markup = btnDisplayAction2?.visible ? (
    <Button
      variant="outline"
      style={{ minWidth: 120 }}
      disabled={
        !btnDisplayAction2.enabled ||
        !btnDisplayAction2.visible ||
        !btnDisplayAction2.content
      }
      onPress={() => btnDisplayAction2Click()}
    >
      <Text style={{ color: "#fff", fontFamily: "Gilmer-Bold", fontSize: 17 }}>
        {btnDisplayAction2.content}
      </Text>
    </Button>
  ) : null;

  const backBtnMarkup = backButton.visible ? (
    <Button
      variant="outline"
      style={{ minWidth: 120 }}
      onPress={() => navigation.goBack()}
      disabled={!backButton.enabled}
    >
      <Text style={{ color: "#fff", fontFamily: "Gilmer-Bold", fontSize: 17 }}>
        Back
      </Text>
    </Button>
  ) : null;

  const tryAgainBtnMarkup = tryAgainButton.visible ? (
    <Button
      variant="outline"
      style={{ minWidth: 120 }}
      onPress={() => doPowerRecovery()}
      disabled={!tryAgainButton.enabled}
    >
      <Text style={{ color: "#fff", fontFamily: "Gilmer-Bold", fontSize: 17 }}>
        {tryAgainButton.content}
      </Text>
    </Button>
  ) : null;

  const reprintReceiptBtnMarkup = reprintReceiptButton.visible ? (
    <Button
      variant="outline"
      style={{ minWidth: 120 }}
      onPress={() => printLastReceipt()}
      disabled={!reprintReceiptButton.enabled}
    >
      <Text style={{ color: "#fff", fontFamily: "Gilmer-Bold", fontSize: 17 }}>
        {reprintReceiptButton.content}
      </Text>
    </Button>
  ) : null;

  const btnMarkup = (btn1Markup ||
    btn2Markup ||
    backBtnMarkup ||
    reprintReceiptBtnMarkup) && (
    <View style={{ marginTop: Spacings.s4 }}>
      <Stack centerHorizontal>
        {btn1Markup}
        {btn2Markup}
        {backBtnMarkup}
        {tryAgainBtnMarkup}
        {reprintReceiptBtnMarkup}
      </Stack>
    </View>
  );

  const isLoading =
    showLoading || (!btnMarkup && !displayTextMarkup && !errorTextMarkup);

  const loadingMarkup = isLoading ? <LoadingIndicator /> : undefined;

  /*
  const responseTextMarkup =
    responseText && !displayTextMarkup && !errorTextMarkup ? (
      <Text
        style={{
          textAlign: "center",
          fontSize: 18,
          marginBottom: Spacings.s6,
          color: colors.white,
        }}
      >
        {responseText}
      </Text>
    ) : null;
    */

  const responseTextMarkup = responseText ? (
    <Text
      style={{
        textAlign: "center",
        fontSize: 18,
        marginBottom: Spacings.s6,
        color: colors.white,
      }}
    >
      {responseText}
    </Text>
  ) : null;

  const sessionInterupted = React.useMemo(() => {
    return linklyPowerRecovery.lastSessionInterrupted;
  }, []);

  const transactionHeaderMarkup = !sessionInterupted ? (
    <View
      style={{
        alignItems: "center",
        marginBottom: Spacings.s4,
      }}
    >
      <Text
        style={{
          textAlign: "center",
          fontSize: 20,
          opacity: 0.8,
          marginBottom: Spacings.s2,
          color: colors.white,
        }}
      >
        {isRefundAmount ? "Refund" : "Purchase"}
      </Text>
      <Text
        style={{
          fontSize: 30,
          textAlign: "center",
          marginBottom: Spacings.s4,
          color: colors.white,
        }}
      >
        {moneyToFormattedString(Math.abs(Number(tenderedAmountInput)))}
      </Text>
    </View>
  ) : (
    <View
      style={{
        alignItems: "center",
        marginBottom: Spacings.s4,
      }}
    >
      <Text
        style={{
          textAlign: "center",
          fontSize: 20,
          opacity: 0.8,
          marginBottom: Spacings.s2,
          color: colors.white,
        }}
      >
        Transaction Recovery
      </Text>
    </View>
  );

  // Escape hatch shown while a recovery is in progress and the screen would
  // otherwise be stuck on the loading spinner with no actionable buttons.
  const cancelRecoveryBtnMarkup =
    sessionInterupted && isLoading ? (
      <View style={{ marginTop: Spacings.s4 }}>
        <Stack centerHorizontal>
          <Button
            variant="outline"
            style={{ minWidth: 120 }}
            onPress={() => handleCancelRecovery()}
          >
            <Text
              style={{
                color: "#fff",
                fontFamily: "Gilmer-Bold",
                fontSize: 17,
              }}
            >
              Cancel Recovery
            </Text>
          </Button>
        </Stack>
      </View>
    ) : null;

  return (
    <View
      flex
      style={{
        width: "100%",
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <View style={{ justifyContent: "center", alignItems: "center" }}>
        <View style={{ marginBottom: Spacings.s5 * 2 }}>
          {assets ? (
            <Image style={{ width: 117, height: 45 }} source={assets[0]} />
          ) : null}
        </View>
        {transactionHeaderMarkup}
        {loadingMarkup}
        {displayTextMarkup}
        {responseTextMarkup}
        {errorTextMarkup}
        {btnMarkup}
        {cancelRecoveryBtnMarkup}
      </View>
    </View>
  );
};

const toReceiptAutoPrintEnumValue = (
  config: LinklyConfiguration
): ReceiptAutoPrint => {
  switch (config.receiptAutoPrint) {
    case ReceiptAutoPrintType.Both:
      return ReceiptAutoPrint.Both;
    case ReceiptAutoPrintType.PinPad:
      return ReceiptAutoPrint.PinPad;
    case ReceiptAutoPrintType.POS:
      return ReceiptAutoPrint.POS;
    default:
      return ReceiptAutoPrint.POS;
  }
};

function generateTxnRef(): string {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let txnRef = "";

  for (let i = 0; i < 10; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    txnRef += characters.charAt(randomIndex);
  }

  return txnRef;
}
